// 챗봇 공과 카드 공유: 링크 복사, 카카오톡 공유, 부모님 안내문 (docs/chatbot-work-plan.md F6)
import axios from "axios";
import { semesterName, lessonName } from "@/assets/js/hanaLessons";

const KAKAO_JS_KEY = "0aca12fa90bba7a078a5a90de8846816"; // kakaoLoginComponent 와 같은 JavaScript 키

// 공과를 다시 묻는 질문 (공유 링크를 열면 챗봇이 이 질문으로 열린다)
export const lessonQuestion = (lesson) =>
  `${semesterName(lesson)} ${lesson.isSpecial ? lesson.occasion + " 공과" : lessonName(lesson)} 알려 줘`;

// 메인 화면에서 챗봇을 이 질문으로 여는 주소 (toTopComponent 가 ?chat= 을 읽는다)
export const shareUrlOf = (lesson) => `${window.location.origin}/?chat=${encodeURIComponent(lessonQuestion(lesson))}`;

export const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    // 권한이 없거나 오래된 브라우저: 숨긴 입력칸으로 복사
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  }
};

// 카카오톡 공유. SDK 가 없거나 실패하면 기기 공유창, 그것도 없으면 false (호출한 쪽이 복사로 대신한다)
// 카카오 텍스트 템플릿은 200자까지라, 더 긴 글(부모 안내문 등)은 잘리지 않도록 기기 공유창이나 복사로 보낸다
const KAKAO_TEXT_LIMIT = 200;

export const shareToKakao = async ({ text, url }) => {
  const Kakao = window.Kakao;
  try {
    if (Kakao && text.length <= KAKAO_TEXT_LIMIT) {
      if (!Kakao.isInitialized()) Kakao.init(KAKAO_JS_KEY);
      const api = Kakao.Share || Kakao.Link; // SDK v2 는 Share, v1 은 Link
      if (api && api.sendDefault) {
        api.sendDefault({ objectType: "text", text, link: { webUrl: url, mobileWebUrl: url } });
        return true;
      }
    }
  } catch (error) {
    console.error(error);
  }
  if (navigator.share) {
    try {
      await navigator.share({ text, url });
      return true;
    } catch (e) {
      return false;
    }
  }
  return false;
};

// 가정예배영상 링크: 공과 카드의 '가정예배' 자료에서 첫 영상의 링크를 구한다 (없으면 null)
export const familyWorshipUrl = async (sections) => {
  const item = (sections || []).find((s) => s.division === "가정예배")?.items?.[0];
  if (!item) return null;
  try {
    const { data } = await axios.get(`/new_api/app_bible/content/${item.id}`);
    const video = data?.data?.contentVideoList?.[0];
    const url = video?.videoLinkHigh || video?.videoLinkNormal;
    return /^https?:\/\//i.test(url || "") ? url : `${window.location.origin}/mall/view/${item.id}`;
  } catch (e) {
    return `${window.location.origin}/mall/view/${item.id}`;
  }
};

// 받침에 따라 을/를 (한글로 끝나지 않으면 "을(를)")
const objectParticle = (word) => {
  const code = (word || "").charCodeAt(word.length - 1) - 0xac00;
  if (code < 0 || code > 11171) return "을(를)";
  return code % 28 ? "을" : "를";
};

// 부모님께 보낼 안내문. 공과 정보는 브로슈어 데이터 그대로 쓰고, 지어낸 내용은 넣지 않는다.
export const buildParentNotice = (lesson, { groupLabel = "", videoUrl = null } = {}) =>
  [
    `[하나 바이블] ${groupLabel ? groupLabel + " " : ""}이번 주 공과 안내`,
    "",
    `이번 주에 아이들이 '${lesson.title}'${objectParticle(lesson.title)} 배웠어요.`,
    "",
    `• 공과: ${semesterName(lesson)} ${lesson.isSpecial ? lesson.occasion : lessonName(lesson)}`,
    `• 본문: ${lesson.passage}`,
    `• 외울말씀: ${lesson.memoryVerseFull || lesson.memoryVerse}`,
    `• 배운 내용: ${lesson.keyPoint}`,
    "",
    "가정에서 본문과 외울말씀을 함께 읽고, 배운 내용을 아이와 이야기 나눠 주세요.",
    ...(videoUrl ? ["", `가정예배영상: ${videoUrl}`] : []),
  ].join("\n");
